import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, FileText, Eye, DollarSign, Calendar, CheckCircle, Clock, AlertCircle, Filter, Loader, RefreshCw } from 'lucide-react';
import { WarehouseInvoice } from '@shared/sdk-v1/models';
import { WarehouseInvoiceApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../shared/api-config';
import { useSelector } from 'react-redux';
import { getUser } from '@state/authSlice';
import { getCourier, getCurrency } from '@state/courierSlice';
import { formatCurrency, formatDualCurrency } from '../utils/currencyUtils';
import { useTrackEvent } from '../utils/track';

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector(getUser);
  const courier = useSelector(getCourier);
  const track = useTrackEvent();

  const [invoices, setInvoices] = useState<WarehouseInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const statusOptions = ['All', 'sent', 'paid', 'overdue'];

  // Fire a debounced `search` event once the user stops typing (never per keystroke).
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) return;
    const timeout = setTimeout(() => {
      track('search', { search_term: term, search_context: 'invoices' });
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm, track]);
  const currency = useSelector(getCurrency);
  const settings = courier?.courierSetting;
  // Dual JMD<->USD display only applies to JMD/USD couriers (single exchangeRate).
  const showDual =
    settings?.showDualCurrency &&
    settings?.exchangeRate &&
    (currency === 'JMD' || currency === 'USD');

  const formatAmount = (amount: number | undefined | null): string => {
    if (showDual) {
      return formatDualCurrency(amount, !!settings?.useJmd, settings?.exchangeRate || 1);
    }
    return formatCurrency(amount, currency);
  };

  // Load invoices
  const loadInvoices = async (isRefresh = false) => {
    if (!user?.id) return;
    
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const api = new WarehouseInvoiceApi(apiConfig());
      const filters: any = {};
      
      if (selectedStatus !== 'All') {
        filters.status = selectedStatus;
      }

      const result = await api.warehouseInvoiceGetMyInvoices({
        subCourierId: user.id as number,
        filters
      });

      setInvoices(result);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [user?.id, selectedStatus]);

  // Filter invoices based on search term
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice =>
      invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.manifest?.manifestNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [invoices, searchTerm]);

  // Get status badge styling
  const getStatusBadge = (status?: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'paid':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'sent':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'overdue':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  // Get status icon
  const getStatusIcon = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'sent':
        return <Clock className="w-4 h-4" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-96 space-y-4">
        <Loader className="animate-spin h-8 w-8 text-blue-600" />
        <p className="text-gray-600 dark:text-gray-400">Loading invoices...</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invoices</h1>
          <p className="text-gray-600 dark:text-gray-400">View and manage your warehouse invoices</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => loadInvoices(true)}
            disabled={isRefreshing}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 
                     text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 
                     bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 
                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search invoices by number or manifest..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white 
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Status Filter */}
        <div className="relative">
          <button
            onClick={() => setShowStatusFilter(!showStatusFilter)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 
                     text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 
                     bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 
                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Filter className="w-4 h-4 mr-2" />
            Status: {selectedStatus}
          </button>

          {showStatusFilter && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg 
                          border border-gray-300 dark:border-gray-600 z-10">
              <div className="py-1">
                {statusOptions.map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      if (status !== 'All') {
                        track('filter_applied', {
                          filter_type: 'status',
                          filter_value: status,
                          context: 'invoices',
                        });
                      }
                      setSelectedStatus(status);
                      setShowStatusFilter(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700
                              ${selectedStatus === status ? 'bg-blue-50 dark:bg-blue-900 text-blue-900 dark:text-blue-100' : 
                                'text-gray-900 dark:text-white'}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Invoices List */}
      {filteredInvoices.length === 0 && !isLoading ? (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No invoices found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm ? 'Try adjusting your search criteria.' : 'You don\'t have any invoices yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => (
            <div
              key={invoice.id}
              className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {getStatusIcon(invoice.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                          Invoice #{invoice.invoiceNumber || 'N/A'}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span>Manifest: {invoice.manifest?.manifestNumber || 'N/A'}</span>
                          <span>•</span>
                          <span>{invoice.packageCount || 0} packages</span>
                          {invoice.dueDate && (
                            <>
                              <span>•</span>
                              <span className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                Due: {new Date(invoice.dueDate).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatAmount(invoice.grandTotal || 0)}
                      </div>
                      <div className={getStatusBadge(invoice.status)}>
                        {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1) || 'Unknown'}
                      </div>
                    </div>

                    <Link
                      to={`/invoices/${invoice.id}`}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm 
                               font-medium rounded-md text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900 
                               hover:bg-blue-200 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 
                               focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Link>
                  </div>
                </div>

                {/* Mobile-friendly summary */}
                <div className="mt-4 sm:hidden">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Total:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {formatAmount(invoice.grandTotal || 0)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                      <span className="ml-2">
                        <span className={getStatusBadge(invoice.status)}>
                          {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1) || 'Unknown'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Invoices;